import { Component, input, output, signal, computed, effect, ContentChild, TemplateRef, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../shared/material/material.module';
import { SearchStepIndicatorComponent } from '../search-step-indicator/search-step-indicator.component';
import { WizardConfig, WizardStep } from '../models/search-state.models';
import { slideAnimation, fadeIn } from '../shared/animations';
import { DeviceDetectorService } from 'ngx-device-detector';

/**
 * Componente que gerencia o fluxo do wizard multi-etapas para configuração de busca.
 * 
 * Funcionalidades:
 * - Gerencia navegação entre etapas
 * - Valida etapas antes de permitir progressão
 * - Preserva estado ao navegar entre etapas
 * - Adiciona animações de transição entre etapas
 * - Usa Angular signals para gerenciamento de estado reativo
 * 
 * Requisitos: 1.2, 1.3, 1.4, 11.1 - Animações de slide para mudança de etapas
 * 
 * @example
 * <app-search-wizard 
 *   [steps]="wizardSteps"
 *   [currentStepIndex]="currentStep"
 *   (stepChange)="onStepChange($event)"
 *   (complete)="onWizardComplete()">
 *   <ng-template #step0>
 *     <!-- Conteúdo da etapa 1 -->
 *   </ng-template>
 *   <ng-template #step1>
 *     <!-- Conteúdo da etapa 2 -->
 *   </ng-template>
 *   <ng-template #step2>
 *     <!-- Conteúdo da etapa 3 -->
 *   </ng-template>
 * </app-search-wizard>
 */
@Component({
  selector: 'app-search-wizard',
  standalone: true,
  imports: [CommonModule, MaterialModule, SearchStepIndicatorComponent],
  templateUrl: './search-wizard.component.html',
  styleUrls: ['./search-wizard.component.scss'],
  animations: [slideAnimation, fadeIn]
})
export class SearchWizardComponent {
  private deviceService = inject(DeviceDetectorService);
  
  /**
   * Lista de etapas do wizard
   */
  steps = input.required<WizardStep[]>();

  /**
   * Índice da etapa inicial (padrão: 0)
   */
  initialStepIndex = input<number>(0);

  /**
   * Função de validação customizada para cada etapa
   * Recebe o índice da etapa e retorna true se válida
   */
  stepValidator = input<(stepIndex: number) => boolean>();

  /**
   * Evento emitido quando a etapa atual muda
   */
  stepChange = output<number>();

  /**
   * Evento emitido quando o wizard é completado (última etapa concluída)
   */
  complete = output<void>();

  /**
   * Evento emitido quando o usuário clica em "Anterior"
   */
  previousClick = output<number>();

  /**
   * Evento emitido quando o usuário clica em "Próximo"
   */
  nextClick = output<number>();

  /**
   * Índice da etapa atual (gerenciado internamente)
   */
  currentStepIndex = signal<number>(0);

  /**
   * Histórico de navegação entre etapas
   */
  private navigationHistory = signal<number[]>([]);

  /**
   * Estado preservado de cada etapa (para manter dados ao navegar)
   */
  private stepStates = signal<Map<number, any>>(new Map());

  /**
   * Configuração computada do wizard
   */
  wizardConfig = computed<WizardConfig>(() => {
    const currentIndex = this.currentStepIndex();
    const steps = this.steps();
    
    return {
      steps: steps,
      currentStepIndex: currentIndex,
      canProceed: this.canProceedToNext(),
      canGoBack: this.canGoBack()
    };
  });

  /**
   * Etapa atual computada
   */
  currentStep = computed(() => {
    const index = this.currentStepIndex();
    return this.steps()[index];
  });

  /**
   * Verifica se está em dispositivo móvel
   */
  isMobile = computed(() => this.deviceService.isMobile());

  /**
   * Suporte a gestos de swipe em mobile
   */
  private touchStartX = 0;
  private touchEndX = 0;
  private readonly SWIPE_THRESHOLD = 50; // pixels mínimos para considerar um swipe

  /**
   * Verifica se pode avançar para a próxima etapa
   */
  private canProceedToNext(): boolean {
    const currentIndex = this.currentStepIndex();
    const steps = this.steps();
    
    // Não pode avançar se já está na última etapa
    if (currentIndex >= steps.length - 1) {
      return false;
    }
    
    // Verifica se a etapa atual é válida
    const currentStepValid = this.validateStep(currentIndex);
    
    return currentStepValid;
  }

  /**
   * Verifica se pode voltar para a etapa anterior
   */
  private canGoBack(): boolean {
    return this.currentStepIndex() > 0;
  }

  /**
   * Valida uma etapa específica
   */
  private validateStep(stepIndex: number): boolean {
    const steps = this.steps();
    const step = steps[stepIndex];
    
    if (!step) {
      return false;
    }
    
    // Usa validador customizado se fornecido
    const customValidator = this.stepValidator();
    if (customValidator) {
      return customValidator(stepIndex);
    }
    
    // Caso contrário, usa a propriedade isValid da etapa
    return step.isValid;
  }

  /**
   * Inicializa o componente
   */
  constructor() {
    // Efeito para inicializar o índice da etapa
    effect(() => {
      const initial = this.initialStepIndex();
      if (this.currentStepIndex() === 0 && initial !== 0) {
        this.currentStepIndex.set(initial);
        this.navigationHistory.set([initial]);
      }
    });
  }

  /**
   * Avança para a próxima etapa
   */
  nextStep(): void {
    const currentIndex = this.currentStepIndex();
    const steps = this.steps();
    
    // Valida a etapa atual
    if (!this.validateStep(currentIndex)) {
      console.warn('Etapa atual não é válida');
      return;
    }
    
    // Verifica se não está na última etapa
    if (currentIndex >= steps.length - 1) {
      // Se está na última etapa e é válida, emite evento de conclusão
      this.complete.emit();
      return;
    }
    
    // Marca a etapa atual como completa
    this.markStepComplete(currentIndex);
    
    // Avança para a próxima etapa
    const nextIndex = currentIndex + 1;
    this.goToStep(nextIndex);
    
    // Emite evento de próximo
    this.nextClick.emit(nextIndex);
  }

  /**
   * Volta para a etapa anterior
   */
  previousStep(): void {
    const currentIndex = this.currentStepIndex();
    
    if (currentIndex <= 0) {
      return;
    }
    
    const previousIndex = currentIndex - 1;
    this.goToStep(previousIndex);
    
    // Emite evento de anterior
    this.previousClick.emit(previousIndex);
  }

  /**
   * Navega para uma etapa específica
   */
  goToStep(stepIndex: number): void {
    const steps = this.steps();
    
    // Valida o índice
    if (stepIndex < 0 || stepIndex >= steps.length) {
      console.warn('Índice de etapa inválido:', stepIndex);
      return;
    }
    
    // Verifica se a etapa é acessível
    if (!this.isStepAccessible(stepIndex)) {
      console.warn('Etapa não é acessível:', stepIndex);
      return;
    }
    
    // Preserva o estado da etapa atual antes de navegar
    this.preserveCurrentStepState();
    
    // Atualiza o índice atual
    const previousIndex = this.currentStepIndex();
    this.currentStepIndex.set(stepIndex);
    
    // Adiciona ao histórico de navegação
    const history = this.navigationHistory();
    this.navigationHistory.set([...history, stepIndex]);
    
    // Emite evento de mudança de etapa
    this.stepChange.emit(stepIndex);
  }

  /**
   * Verifica se uma etapa é acessível
   * Uma etapa é acessível se:
   * - É a etapa atual
   * - É uma etapa anterior (já visitada)
   * - É a próxima etapa e a atual está válida
   */
  private isStepAccessible(stepIndex: number): boolean {
    const currentIndex = this.currentStepIndex();
    
    // Etapa atual sempre é acessível
    if (stepIndex === currentIndex) {
      return true;
    }
    
    // Etapas anteriores são acessíveis
    if (stepIndex < currentIndex) {
      return true;
    }
    
    // Próxima etapa é acessível se a atual for válida
    if (stepIndex === currentIndex + 1) {
      return this.validateStep(currentIndex);
    }
    
    // Outras etapas não são acessíveis
    return false;
  }

  /**
   * Marca uma etapa como completa
   */
  private markStepComplete(stepIndex: number): void {
    const steps = this.steps();
    const step = steps[stepIndex];
    
    if (step) {
      step.isComplete = true;
    }
  }

  /**
   * Preserva o estado da etapa atual
   */
  private preserveCurrentStepState(): void {
    const currentIndex = this.currentStepIndex();
    const states = this.stepStates();
    
    // Aqui você pode adicionar lógica para capturar o estado da etapa
    // Por exemplo, valores de formulário, seleções, etc.
    // Por enquanto, apenas marca que a etapa foi visitada
    states.set(currentIndex, { visited: true, timestamp: Date.now() });
    
    this.stepStates.set(new Map(states));
  }

  /**
   * Recupera o estado preservado de uma etapa
   */
  getStepState(stepIndex: number): any {
    return this.stepStates().get(stepIndex);
  }

  /**
   * Valida a etapa atual
   */
  validateCurrentStep(): boolean {
    return this.validateStep(this.currentStepIndex());
  }

  /**
   * Reseta o wizard para o estado inicial
   */
  reset(): void {
    this.currentStepIndex.set(0);
    this.navigationHistory.set([0]);
    this.stepStates.set(new Map());
    
    // Reseta o estado de todas as etapas
    const steps = this.steps();
    steps.forEach(step => {
      step.isValid = false;
      step.isComplete = false;
    });
  }

  /**
   * Manipula o clique no indicador de etapas
   */
  onStepIndicatorClick(stepIndex: number): void {
    this.goToStep(stepIndex);
  }

  /**
   * Retorna o progresso percentual do wizard
   */
  getProgress(): number {
    const total = this.steps().length;
    const current = this.currentStepIndex();
    return total > 0 ? ((current + 1) / total) * 100 : 0;
  }

  /**
   * Verifica se o wizard está completo
   */
  isComplete(): boolean {
    const steps = this.steps();
    return steps.every(step => step.isComplete);
  }

  /**
   * Verifica se está na primeira etapa
   */
  isFirstStep(): boolean {
    return this.currentStepIndex() === 0;
  }

  /**
   * Verifica se está na última etapa
   */
  isLastStep(): boolean {
    return this.currentStepIndex() === this.steps().length - 1;
  }

  /**
   * Manipula início do toque (para swipe)
   */
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.isMobile()) return;
    this.touchStartX = event.changedTouches[0].screenX;
  }

  /**
   * Manipula fim do toque (para swipe)
   */
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.isMobile()) return;
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipeGesture();
  }

  /**
   * Processa gesto de swipe
   */
  private handleSwipeGesture(): void {
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    // Swipe para a direita (voltar)
    if (swipeDistance > this.SWIPE_THRESHOLD && this.canGoBack()) {
      this.previousStep();
    }
    
    // Swipe para a esquerda (avançar)
    if (swipeDistance < -this.SWIPE_THRESHOLD && this.canProceedToNext()) {
      this.nextStep();
    }
  }
}
